"use client";

import React, { useState, useRef, useEffect } from "react";
import { useWorkspace } from "@/lib/context/WorkspaceContext";
import { AssignmentContext, shouldDisplayRole } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import { LogOut, ChevronDown, User, Layers, Shield, LayoutDashboard, Settings } from "lucide-react";
import { useRouter } from "next/navigation";

export default function UserProfileDropdown() {
    const { user, activeContext, setActiveContext, logout } = useWorkspace();
    const router = useRouter();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    if (!user) return null;

    const assignments = user.assignments || [];
    const currentLabel = activeContext 
        ? `${activeContext.title || "Group " + activeContext.entity_id}`
        : "Global Workspace";

    return (
        <div className="relative" ref={dropdownRef}>
            <div className="flex items-center bg-white border-2 border-[#003d45]/10 rounded-full transition-all group shadow-sm overflow-hidden">
                {/* Dashboard Navigation Section */}
                <button
                    onClick={() => router.push("/dashboard")}
                    className="flex items-center gap-3 p-1.5 pl-1.5 pr-3 hover:bg-[#003d45]/5 transition-all outline-none border-r border-[#003d45]/10"
                    title="Go to Dashboard"
                >
                    <div className="w-8 h-8 rounded-full bg-[#003d45] text-white flex items-center justify-center font-black text-xs border-2 border-white shadow-sm group-hover:scale-105 transition-transform">
                        {user.first_name?.[0]?.toUpperCase() || "U"}
                    </div>
                    <div className="hidden md:flex flex-col items-start leading-tight text-left">
                        <span className="text-[10px] font-black uppercase tracking-widest text-[#003d45]">
                            {user.first_name}
                        </span>
                        <span className="text-[9px] font-bold text-[#468892]/70 uppercase tracking-tighter">
                            {currentLabel}
                        </span>
                    </div>
                </button>

                {/* Dropdown Toggle Section */}
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={cn(
                        "p-2.5 px-3 hover:bg-[#003d45]/5 transition-all outline-none flex items-center justify-center",
                        isOpen && "bg-[#003d45]/10 text-[#003d45]"
                    )}
                    aria-label="Toggle profile menu"
                >
                    <ChevronDown className={cn("w-4 h-4 text-[#003d45] transition-transform duration-200", isOpen && "rotate-180")} />
                </button>
            </div>

            {isOpen && (
                <div className="absolute top-full mt-3 w-72 bg-white rounded-2xl shadow-2xl shadow-[#003d45]/20 border border-[#003d45]/10 flex flex-col overflow-hidden z-[60] right-0 animate-in fade-in slide-in-from-top-2 duration-200">
                    
                    {/* User Header */}
                    <div className="px-6 py-5 bg-[#003d45]/5 border-b border-[#003d45]/5">
                        <p className="text-[10px] font-black uppercase tracking-widest text-[#003d45]/40 mb-1">Authenticated As</p>
                        <p className="text-sm font-black text-[#003d45]">{user.first_name} {user.last_name}</p>
                        <p className="text-[10px] font-bold text-[#468892] lowercase mt-0.5">{user.email}</p>
                        {shouldDisplayRole(user.role) && (
                            <div className="mt-3 flex gap-2">
                                 <span className="px-2 py-0.5 bg-[#003d45] text-white rounded text-[8px] font-black uppercase tracking-widest">
                                    {user.role}
                                 </span>
                            </div>
                        )}
                    </div>

                    {/* Footer Actions */}
                    <div className="p-2">
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                router.push("/dashboard");
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#003d45]/5 text-[#003d45] transition-colors group"
                        >
                            <div className="w-8 h-8 rounded-lg bg-[#003d45]/5 group-hover:bg-[#003d45]/10 flex items-center justify-center transition-colors">
                                <LayoutDashboard className="w-4 h-4" />
                            </div>
                            <span className="text-xs font-black uppercase tracking-widest">My Dashboard</span>
                        </button>
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                router.push("/account");
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#003d45]/5 text-[#003d45] transition-colors group"
                        >
                            <div className="w-8 h-8 rounded-lg bg-[#003d45]/5 group-hover:bg-[#003d45]/10 flex items-center justify-center transition-colors">
                                <Settings className="w-4 h-4" />
                            </div>
                            <span className="text-xs font-black uppercase tracking-widest">Account Settings</span>
                        </button>
                        <button
                            onClick={() => {
                                setIsOpen(false);
                                logout();
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-50 text-red-600 transition-colors group"
                        >
                            <div className="w-8 h-8 rounded-lg bg-red-50 group-hover:bg-red-100 flex items-center justify-center transition-colors">
                                <LogOut className="w-4 h-4" />
                            </div>
                            <span className="text-xs font-black uppercase tracking-widest">Sign Out</span>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
